import { reactive, ref, unref, watch, type ModelRef, type Ref } from "vue";
import { z } from "zod";

type WrapWithModelRef<T> = {
	[K in keyof T]: T[K] extends infer U ? ModelRef<U | undefined> : never;
};

type WrapWithRef<T> = {
	[K in keyof T]: T[K] extends infer U ? Ref<U | undefined> : never;
};

export function useValidation<T extends z.ZodRawShape>(
	value:
		| Partial<z.infer<z.ZodObject<T>>>
		| WrapWithRef<z.infer<z.ZodObject<T>>>
		| WrapWithModelRef<z.infer<z.ZodObject<T>>>,
	validation: z.ZodObject<T>,
) {
	type Shape<U> = Record<keyof T, U>;
	const errors = reactive(
		Object.fromEntries(
			Object.keys(value).map(key => {
				return [key, undefined];
			}),
		),
	) as Shape<string | undefined>;

	const touched = reactive(
		Object.fromEntries(
			Object.keys(value).map(key => {
				return [key, false];
			}),
		),
	) as Shape<boolean>;

	const valid = ref(true);

	const validateField = (key: keyof T) => {
		const rawValue = Object.fromEntries(
			Object.keys(value).map(key => [key, unref(value[key])]),
		) as Partial<z.infer<z.ZodObject<T>>>;
		const result = validation.safeParse(rawValue);
		if (result.success) {
			errors[key] = undefined;
			return;
		} else {
			const resultErrors = result.error.flatten();
			if (resultErrors.fieldErrors[key]?.[0])
				errors[key] = resultErrors.fieldErrors[key]?.[0];
			else errors[key] = undefined;
		}
		valid.value = Object.values(errors).every(error => error === undefined);
	};

	const touch = Object.fromEntries(
		Object.keys(value).map((key: keyof T) => {
			return [
				key,
				() => {
					touched[key] = true;
					validateField(key);
				},
			];
		}),
	) as unknown as Shape<() => void>;

	Object.keys(value).forEach((key: keyof T) => {
		watch(
			() => value[key],
			() => {
				if (touched[key]) validateField(key);

				valid.value = Object.values(errors).every(
					error => error === undefined,
				);
			},
		);
	});

	const validate = () => {
		const rawValue = Object.fromEntries(
			Object.keys(value).map(key => [key, unref(value[key])]),
		) as Partial<z.infer<z.ZodObject<T>>>;
		const result = validation.safeParse(rawValue);
		if (result.success) {
			Object.keys(errors).forEach((key: keyof T) => {
				errors[key] = undefined;
			});
			return true;
		}

		const resultErrors = result.error.flatten();
		Object.keys(errors).forEach((key: keyof T) => {
			errors[key] = resultErrors.fieldErrors[key]?.[0];
		});
		return false;
	};

	const reset = () => {
		Object.keys(errors).forEach((key: keyof T) => {
			errors[key] = undefined;
		});
		Object.keys(touched).forEach((key: keyof T) => {
			touched[key] = false;
		});
	};

	const touchAll = () => {
		Object.keys(touched).forEach((key: keyof T) => {
			touched[key] = true;
		});
		validate();
		valid.value = Object.values(errors).every(error => error === undefined);
	};

	return {
		validate,
		errors,
		touched,
		touch,
		touchAll,
		reset,
		valid,
	};
}

export const zNumber = (num: z.ZodNumber) =>
	z.preprocess(v => (v === "" ? undefined : Number(v)), num);
